import * as THREE from 'three';
import { CORRIDOR_CEILING_Y, CORRIDOR_FLOOR_Y } from './corridor';
import type { TerrainId } from './catalog';

/** How far below the corridor floor the Mars surface sits (avoids clipping ships/blocks). */
const MERS_SURFACE_Y = CORRIDOR_FLOOR_Y - 10;
/** Peak dune height (low-poly facets from coarse mesh + flat shade). */
const MERS_DUNE_AMP = 2.4;
/** Half-extent of the camera-centered ground plane (world units). */
const MERS_PLANE_DIST = 340;
/**
 * Scroll vs camera motion. 1 = world-locked ground (true travel feel).
 * Chase cam lags the ship slightly; 1.0 keeps dunes moving with camera travel.
 */
const MERS_SCROLL_GAIN = 1.0;
/** Idle crawl on home screen (units/sec along flight axis). */
const MERS_IDLE_SCROLL = 28;
/** Coarse grid → large low-poly facets over the dune heightfield. */
const MERS_SEGS = 28;

/**
 * Owns sandbox terrain layers on the shared Three.js scene.
 * Horizon = dual infinite grids.
 * Mers = camera-centered low-poly random dunes that stream with travel.
 */
export class SceneEnvironment {
  private terrain: TerrainId = 'horizon';
  private readonly marsRoot = new THREE.Group();
  private groundMat: THREE.ShaderMaterial | null = null;
  private builtMars = false;
  /** Continuous scroll (absolute, not modular mesh wrap). */
  private scrollZ = 0;
  private scrollX = 0;
  private idleAlong = 0;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly floorGrid: THREE.Object3D,
    private readonly ceilingGrid: THREE.Object3D,
    private readonly horizonBackground = 0x23272a,
  ) {
    this.marsRoot.name = 'terrain-mers';
    this.marsRoot.visible = false;
    this.scene.add(this.marsRoot);
  }

  getTerrain(): TerrainId {
    return this.terrain;
  }

  setTerrain(id: TerrainId) {
    if (this.terrain === id) {
      this.apply();
      return;
    }
    this.terrain = id;
    this.apply();
  }

  apply() {
    if (this.terrain === 'mers') {
      this.ensureMars();
      this.floorGrid.visible = false;
      this.ceilingGrid.visible = false;
      this.marsRoot.visible = true;
      this.scene.background = new THREE.Color(0x6fa8c9);
      this.scene.fog = new THREE.Fog(0x8ebdd4, 180, 680);
    } else {
      this.floorGrid.visible = true;
      this.ceilingGrid.visible = true;
      this.marsRoot.visible = false;
      this.scene.background = new THREE.Color(this.horizonBackground);
      this.scene.fog = null;
    }
  }

  dispose() {
    this.scene.remove(this.marsRoot);
    this.marsRoot.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const mats = obj.material;
        if (Array.isArray(mats)) {
          mats.forEach((m) => m.dispose());
        } else {
          mats.dispose();
        }
      }
    });
    this.groundMat = null;
  }

  private ensureMars() {
    if (this.builtMars) {
      return;
    }
    this.builtMars = true;

    const groundGeo = new THREE.PlaneBufferGeometry(2, 2, MERS_SEGS, MERS_SEGS);

    this.groundMat = new THREE.ShaderMaterial({
      side: THREE.DoubleSide,
      depthWrite: true,
      depthTest: true,
      transparent: false,
      extensions: {
        derivatives: true,
      },
      uniforms: {
        uDistance: { value: MERS_PLANE_DIST },
        uSurfaceY: { value: MERS_SURFACE_Y },
        uAmp: { value: MERS_DUNE_AMP },
        // Absolute scroll: set from camera world position each frame
        uScroll: { value: new THREE.Vector2(0, 0) },
        uColorSand: { value: new THREE.Color(0xb84422) },
        uColorShade: { value: new THREE.Color(0x6a2c16) },
        uColorCrest: { value: new THREE.Color(0xe07a45) },
        uFogColor: { value: new THREE.Color(0x8ebdd4) },
        uFogNear: { value: 180 },
        uFogFar: { value: 680 },
      },
      vertexShader: `
        varying vec3 vWorldPos;
        varying float vH;
        uniform float uDistance;
        uniform float uSurfaceY;
        uniform float uAmp;
        uniform vec2 uScroll;

        // Stable 2D hash → [0,1]
        float hash21(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        vec2 hash22(vec2 p) {
          float n = hash21(p);
          return vec2(n, hash21(p + 19.19));
        }

        // Value noise
        float valueNoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash21(i);
          float b = hash21(i + vec2(1.0, 0.0));
          float c = hash21(i + vec2(0.0, 1.0));
          float d = hash21(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }

        // Worley / cellular — random dune “humps” with irregular spacing
        float cellDunes(vec2 p) {
          vec2 i = floor(p);
          float minD = 1.0;
          float minD2 = 1.0;
          for (int y = -1; y <= 1; y++) {
            for (int x = -1; x <= 1; x++) {
              vec2 g = vec2(float(x), float(y));
              vec2 o = hash22(i + g);
              // Jitter feature points so dunes aren’t on a grid
              vec2 r = g + o - fract(p);
              float d = dot(r, r);
              if (d < minD) {
                minD2 = minD;
                minD = d;
              } else if (d < minD2) {
                minD2 = d;
              }
            }
          }
          // Soft mound from nearest site; f2-f1 adds irregular ridges between dunes
          float mound = 1.0 - smoothstep(0.0, 0.85, sqrt(minD));
          float ridge = clamp(sqrt(minD2) - sqrt(minD), 0.0, 1.0);
          return mound * 0.85 + ridge * 0.35;
        }

        // Multi-scale random dunes (continuous → streams under flight without seams)
        float duneHeight(vec2 p) {
          // Large wind-blown dunes
          float d1 = cellDunes(p * 0.045);
          // Medium ripples / secondary dunes
          float d2 = cellDunes(p * 0.09 + 17.3);
          // Fine crust noise
          float n = valueNoise(p * 0.12);
          float h = d1 * 0.72 + d2 * 0.38 + n * 0.12;
          // Slight directional bias (wind along corridor)
          h *= 0.85 + 0.15 * sin(p.x * 0.03 + p.y * 0.02);
          return h * uAmp;
        }

        void main() {
          // Camera-centered coverage (infinite plane, no mesh tiling)
          float lx = position.x * uDistance;
          float lz = position.y * uDistance;

          // Sample space = local offset + world scroll (flight streams dunes past)
          vec2 sampleXZ = vec2(lx, lz) + uScroll;
          float h = duneHeight(sampleXZ);

          vec3 worldPos = vec3(
            cameraPosition.x + lx,
            uSurfaceY + h,
            cameraPosition.z + lz
          );

          vWorldPos = worldPos;
          vH = h / max(uAmp, 0.001);

          gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vWorldPos;
        varying float vH;
        uniform float uDistance;
        uniform vec3 uColorSand;
        uniform vec3 uColorShade;
        uniform vec3 uColorCrest;
        uniform vec3 uFogColor;
        uniform float uFogNear;
        uniform float uFogFar;

        void main() {
          // Flat low-poly faces from geometry (coarse tessellation + derivatives)
          vec3 fdx = dFdx(vWorldPos);
          vec3 fdy = dFdy(vWorldPos);
          vec3 n = normalize(cross(fdx, fdy));
          float ndl = clamp(dot(n, normalize(vec3(0.35, 0.9, 0.2))), 0.0, 1.0);
          float shade = 0.48 + 0.52 * ndl;

          // Height-based sand color: valleys darker, crests warmer
          vec3 col = mix(uColorShade, uColorSand, smoothstep(0.05, 0.55, vH));
          col = mix(col, uColorCrest, smoothstep(0.55, 0.95, vH));
          col *= shade;

          // Slope tint (steeper faces a bit cooler/darker)
          float slope = 1.0 - clamp(n.y, 0.0, 1.0);
          col = mix(col, uColorShade, slope * 0.35);

          float dist = length(vWorldPos.xz - cameraPosition.xz);
          float fogFactor = smoothstep(uFogNear, uFogFar, dist);
          col = mix(col, uFogColor, fogFactor * 0.9);

          float rim = 1.0 - smoothstep(uDistance * 0.8, uDistance * 0.98, dist);
          col = mix(uFogColor, col, rim);

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });

    const ground = new THREE.Mesh(groundGeo, this.groundMat);
    ground.name = 'mers-ground';
    ground.frustumCulled = false;
    this.marsRoot.add(ground);

    // Soft sky band
    const skyGeo = new THREE.PlaneBufferGeometry(2, 2, 1, 1);
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
      uniforms: {
        uDistance: { value: MERS_PLANE_DIST * 1.1 },
        uHeight: { value: CORRIDOR_CEILING_Y + 48 },
        uColor: { value: new THREE.Color(0x9ec8e0) },
      },
      vertexShader: `
        varying float vFade;
        uniform float uDistance;
        uniform float uHeight;
        void main() {
          vec3 worldPos = vec3(
            cameraPosition.x + position.x * uDistance,
            uHeight + position.y * 160.0,
            cameraPosition.z - uDistance * 0.4 + position.y * uDistance * 0.1
          );
          vFade = 1.0 - length(position.xy);
          gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);
        }
      `,
      fragmentShader: `
        varying float vFade;
        uniform vec3 uColor;
        void main() {
          float a = 0.32 * smoothstep(0.05, 0.55, vFade);
          if (a < 0.02) discard;
          gl_FragColor = vec4(uColor, a);
        }
      `,
    });
    const sky = new THREE.Mesh(skyGeo, skyMat);
    sky.name = 'mers-sky';
    sky.frustumCulled = false;
    this.marsRoot.add(sky);
  }

  /**
   * Drive scroll from absolute camera position so travel is unmistakable:
   * flying −Z continuously advances tile UVs (road-band motion), not vertical “breathing”.
   */
  update(camera: THREE.Camera, dt = 0) {
    if (this.terrain !== 'mers' || !this.marsRoot.visible || !this.groundMat) {
      return;
    }

    // Idle crawl when parked on the home screen (same sense as flight: forward stream)
    this.idleAlong -= MERS_IDLE_SCROLL * dt;

    // World-locked sampling. Flight is along −Z (camera.z decreases).
    // Use +camera.z for scroll so ground streams forward under the craft
    // (opposite of the earlier inverted direction).
    this.scrollX = camera.position.x * MERS_SCROLL_GAIN;
    this.scrollZ = camera.position.z * MERS_SCROLL_GAIN + this.idleAlong;

    this.groundMat.uniforms.uScroll.value.set(this.scrollX, this.scrollZ);
  }
}
